import React from "react";
import {
  Atom,
  BookOpen,
  Calculator,
  FlaskConical,
  Globe2,
  Landmark,
  Languages,
  Lightbulb,
  Map,
  Microscope,
  Pencil,
  ScrollText,
  type LucideIcon,
} from "lucide-react";

export type PracticeArtKind = "business-studies" | "french" | "spanish" | "english" | "geography" | "history" | "maths" | "physics" | "science" | "common-science" | "default";

const iconByKind: Record<PracticeArtKind, LucideIcon> = {
  "business-studies": BookOpen,
  french: Languages,
  spanish: Languages,
  english: Pencil,
  geography: Globe2,
  history: ScrollText,
  maths: Calculator,
  physics: Atom,
  science: Microscope,
  "common-science": FlaskConical,
  default: BookOpen,
};

/** Small line-art companion to the existing Elume Student Hub doodles. */
export function SubjectPracticeDoodle({ kind, className = "" }: { kind: PracticeArtKind; className?: string }) {
  const Icon = iconByKind[kind];
  return (
    <svg viewBox="0 0 180 132" aria-hidden="true" className={`h-auto w-full ${className}`} fill="none">
      <path d="M26 111c17-7 29-7 46 0M119 112c12-5 23-5 35 0" stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity=".45" />
      <circle cx="48" cy="39" r="13" stroke="currentColor" strokeWidth="3" />
      <path d="M48 52v29m0-17-15 13m15-13 16 11m-16 6-10 21m10-21 14 21" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M69 78c10 0 17-4 23-12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="3 6" opacity=".75" />
      <g transform="translate(93 20)" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <rect x="0" y="14" width="59" height="48" rx="7" fill="white" fillOpacity=".64" />
        <path d="M29.5 14v48M8 27h13m18 0h12M8 40h9m24 0h10" opacity=".35" />
      </g>
      {kind === "french" && <g transform="translate(105 43)"><path d="M0 0h14v18H0z" fill="#2563eb" stroke="none" /><path d="M14 0h14v18H14z" fill="white" stroke="none" /><path d="M28 0h14v18H28z" fill="#ef4444" stroke="none" /></g>}
      {kind === "geography" && <Map x="106" y="40" width="39" height="39" strokeWidth="2.5" />}
      {kind === "history" && <Landmark x="106" y="40" width="39" height="39" strokeWidth="2.5" />}
      {(kind === "science" || kind === "physics") && <Atom x="106" y="40" width="39" height="39" strokeWidth="2.5" />}
      {kind === "common-science" && <FlaskConical x="106" y="40" width="39" height="39" strokeWidth="2.5" />}
      {(kind === "english" || kind === "maths" || kind === "default") && <Icon x="106" y="40" width="39" height="39" strokeWidth="2.5" />}
      <path d="M152 17v10m-5-5h10M83 14l3 7 7 3-7 3-3 7-3-7-7-3 7-3 3-7Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function PracticeHeroMarks() {
  return (
    <svg viewBox="0 0 180 100" aria-hidden="true" className="absolute right-0 top-0 h-full w-44 text-emerald-700/30 sm:w-52" fill="none">
      <path d="M42 55c15-15 29-15 44 0M22 69c-8 2-12 7-13 14m92-55 7-8m1 13 11-2m-7 12 8 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M130 54l4 9 9 4-9 4-4 9-4-9-9-4 9-4 4-9Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <Lightbulb x="56" y="20" width="25" height="25" strokeWidth="2.5" />
    </svg>
  );
}
